import { toJsxRuntime } from 'hast-util-to-jsx-runtime'
import { useMemo, type ReactNode } from 'react'
import { Fragment, jsx, jsxs } from 'react/jsx-runtime'
import { parseLegal } from '../parse.ts'
import { documentHast } from '../render/hast.ts'
import { documentCss } from '../render/styles.ts'
import type { DocumentMeta, LintDiagnostic, RenderOptions } from '../types.ts'

/**
 * O documento na tela — o mesmo hast e o mesmo CSS do PDF, então o que a paciente lê no celular
 * é o que ela assina e o que a clínica arquiva.
 */

export interface UseLegalDocumentResult {
  element: ReactNode
  meta: DocumentMeta
  diagnostics: LintDiagnostic[]
  variables: string[]
  css: string
}

export function useLegalDocument(
  markdown: string,
  options: RenderOptions = {},
): UseLegalDocumentResult {
  const {
    variables,
    evidence,
    scale,
    mode,
    showHeader,
    numbering,
    clinicName,
    lang,
    anamnesis,
    logo,
  } = options
  return useMemo(() => {
    const doc = parseLegal(markdown, { variables, numbering, anamnesis })
    const hast = documentHast(doc, {
      variables,
      evidence,
      scale,
      mode,
      showHeader,
      numbering,
      clinicName,
      lang,
      logo,
    })
    const element = toJsxRuntime(hast, { Fragment, jsx, jsxs, development: false })
    const css = documentCss({
      footerLeft: evidence ? `Protocolo ${evidence.protocolNumber}` : (doc.meta.title ?? ''),
      footerRight: clinicName ?? 'Westhetics',
    })
    return {
      element,
      meta: doc.meta,
      diagnostics: doc.diagnostics,
      variables: doc.variables,
      css,
    }
    // Objetos são comparados por identidade de propósito: quem chama memoiza `variables`, `evidence`,
    // `anamnesis` e `logo`.
  }, [
    markdown,
    variables,
    evidence,
    scale,
    mode,
    showHeader,
    numbering,
    clinicName,
    lang,
    anamnesis,
    logo,
  ])
}

export interface LegalDocumentProps extends RenderOptions {
  markdown: string
  /** Injeta o CSS do documento junto (padrão). Desligue se a página já carregou `documentCss()`. */
  withStyles?: boolean
  className?: string
}

export function LegalDocument({
  markdown,
  withStyles = true,
  className,
  ...options
}: LegalDocumentProps) {
  const { element, css } = useLegalDocument(markdown, options)
  return (
    <div className={className} data-slot="legal-document">
      {withStyles ? <style>{css}</style> : null}
      {element}
    </div>
  )
}

export { documentCss }
